import { useState } from "react";
import { fmtUSD } from "@/lib/api";
import { Grid } from "./CompGrids";
import { ParcelLink } from "./ParcelCard";

const n = (v) => (v == null ? "-" : Math.round(v).toLocaleString());
const perUnit = (b) => {
  const p = b.county_per;
  if (!p) return "-";
  return <span title={`Size from ${p.size_source}`}>{p.basis === "unit" ? `${fmtUSD(p.value)}/unit` : `$${p.value.toFixed(0)}/SF`}</span>;
};

export function CountyPanel({ study }) {
  const c = study.county;
  if (!c) return null;
  if (!c.county) return <div className="text-[11px] text-slate-500" data-testid="county-unsupported">{c.note}</div>;
  const s = c.subject;
  return (
    <div className="panel p-4 text-xs space-y-1" data-testid="county-subject">
      <div className="eyebrow">Subject on the county roll</div>
      {s ? <div className="flex flex-wrap gap-x-6 gap-y-1">
        <span>{s.district}</span><span>Parcel <ParcelLink county={c.county} pid={s.parcel_id} testid="county-subject-parcel"><span className="num">{s.parcel_id}</span></ParcelLink></span>
        <span>Market value <b className="num" data-testid="county-subject-value">{fmtUSD(s.market_value)}</b></span><span>Land {fmtUSD(s.land_value, true)} · Improvements {fmtUSD(s.improvement_value, true)}</span>
        <span>{s.class || ""}{s.year_built ? ` · built ${s.year_built}` : ""}</span><span>Owner {s.owner}</span><span>Tax year {s.tax_year}</span>
      </div> : <div className="text-slate-500">No parcel found under the subject point.</div>}
      <div className="text-[10px] text-slate-500">{c.note}</div>
    </div>
  );
}

export function Listings({ study }) {
  const l = study.listings || [];
  if (!l.length) return null;
  return (
    <Grid testid="om-listings" title="From offering memoranda - asking prices and OM rents (shown for context; used as comps only after an analyst verifies them in the Comps database)"
      head={["Property", "Dist.", "Type", "Asking / rents", "Size", "Status", "Source"]}
      rows={l.map((r) => [r.name, `${r.distance_mi} mi`, r.kind === "listing" ? "Listing (asking)" : "OM rents", r.kind === "listing" ? `${fmtUSD(r.price, true)}${r.size ? ` · ${fmtUSD(r.price / r.size)}/${study.subject.property_type === "multifamily" ? "unit" : "SF"}` : ""}` : Object.entries(r.rents).map(([k, v]) => `${k} ${fmtUSD(v)}`).join(" · "),
        n(r.size), <span className={r.status === "Verified" ? "text-emerald-700" : "text-amber-700"}>{r.origin || r.status}</span>, <span className="text-[10px] text-slate-500">{r.source_ref}</span>])} />
  );
}

export function NearbyBuildings({ study }) {
  const [all, setAll] = useState(false);
  const full = study.nearby || [];
  const rows = all ? full : full.slice(0, 15);
  const inside = study.analysis.within_radius;
  return (
    <div className="space-y-1">
    <Grid testid="nearby-buildings" title={`Nearby ${study.type_meta.label.toLowerCase()} buildings inside ${study.params.radius_mi} mi - showing ${rows.length} of ${inside} - real OpenStreetMap attributes, blank where not mapped`}
      head={["Building", "Address", "Dist.", "Levels", "Units", "Footprint SF", "Est. GBA SF", "Built", "Appraisal district value", "Value / unit or SF", "Owner (county roll)", "Comp data"]}
      rows={rows.map((b) => [b.website ? <a href={b.website} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{b.name}</a> : b.name, b.address || "-", `${b.distance_mi.toFixed(2)} mi`, b.levels ?? "-", n(b.units), n(b.footprint_sf), n(b.gba_sf), b.year_built || b.county?.year_built || "-",
        b.county?.market_value ? <span><ParcelLink county={study.county?.county} pid={b.county.parcel_id} testid={`county-value-${b.osm_id}`}>{fmtUSD(b.county.market_value, true)}</ParcelLink>{b.county_check && <span className="block text-[10px] text-amber-700 max-w-[220px]" data-testid={`county-check-${b.osm_id}`} title={b.county_check}>parcel may not match: {b.county_check}</span>}</span> : "-",
        perUnit(b), b.county?.owner || "-",
        b.sale_source || b.rent_source ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{b.sale_source || b.rent_source}</span> : <span className="text-slate-400">None on file</span>])} />
    {full.length > 15 && <button data-testid="nearby-toggle" onClick={() => setAll(!all)} className="text-[11px] text-sky-700 hover:underline">{all ? "Show fewer" : `Show all ${full.length} listed (nearest first)`}</button>}
    </div>
  );
}

export function AcsBenchmark({ study }) {
  const acs = study.acs || [];
  if (!acs.length) return null;
  const keys = ["Studio", "1BR", "2BR", "3BR", "All units"];
  const b = study.analysis.acs_benchmark || [];
  return (
    <div className="space-y-2" data-testid="acs-benchmark">
      <Grid testid="acs-zip-grid" title={`Neighbourhood rent benchmark - U.S. Census ACS ${acs[0].years || ""} median gross rent by ZIP`}
        head={["ZIP", ...keys, "Renter households", "Rental vacancy"]}
        rows={[...acs.map((d) => [d.zcta, ...keys.map((k) => (d.rents[k] ? fmtUSD(d.rents[k]) : d.top_coded?.includes(k) ? "$3,500+" : "-")), n(d.renter_households), d.rental_vacancy != null ? `${(d.rental_vacancy * 100).toFixed(1)}%` : "-"]),
          ["Median of ZIPs", ...keys.map((k) => { const x = b.find((c) => c.unit_type === k); return x ? <b>{fmtUSD(x.median)}</b> : "-"; }), "", ""]]} />
      <div className="text-[10px] text-slate-500">{acs[0].source}. Gross rent includes utilities and covers all renter households (houses and apartments), so treat it as a neighbourhood benchmark, not a building comp. ZIPs = subject plus ZIPs tagged on nearby buildings.</div>
    </div>
  );
}

export function Valuations({ study }) {
  const v = study.valuations || [];
  if (!v.length) return null;
  const mf = study.subject.property_type === "multifamily";
  return (
    <Grid testid="loan-valuations" title="Valuation evidence from your OmniFi loan files (appraised values - not sales, not used in the value conclusion)"
      head={["Loan", "Dist.", mf ? "Units" : "SF", "Appraised value", mf ? "$/unit" : "$/SF", "Cap rate"]}
      rows={v.map((x) => [x.loan, `${x.distance_mi} mi`, n(x.size), fmtUSD(x.value, true), mf ? fmtUSD(x.per_unit) : `$${x.per_unit.toFixed(2)}`, x.cap_rate ? `${(x.cap_rate * 100).toFixed(2)}%` : "-"])} />
  );
}
